import { useMemo, useState } from 'react'
import { useStore } from '../store/AppStore'
import { eventsOn, upcomingEvents } from '../store/selectors'
import {
  fmtDate,
  fmtMonthYear,
  monthGrid,
  monthOf,
  num,
  relDay,
  shiftMonth,
  today,
  weekdayNames,
} from '../lib/date'
import { Button, Card, Empty, Field, Meta, ScreenHead, Sheet, hueVars } from '../components/ui'
import { IconChevron, IconPlus, IconTrash } from '../components/icons'
import { useT, type TKey } from '../i18n'
import { EVENT_KINDS, type CalEvent, type EventKind, type ISODate } from '../store/types'

const hueOf = (kind: EventKind) => EVENT_KINDS.find((k) => k.id === kind)?.hue ?? 'clay'

export default function Calendar() {
  const { state, addEvent, updateEvent, removeEvent } = useStore()
  const tr = useT()
  const dow = weekdayNames('narrow')
  const t = today()
  const [anchor, setAnchor] = useState<ISODate>(t)
  const [selected, setSelected] = useState<ISODate>(t)
  const [editing, setEditing] = useState<CalEvent | 'new' | null>(null)
  const [view, setView] = useState<'month' | 'agenda'>('month')

  const grid = useMemo(() => monthGrid(anchor), [anchor])
  const anchorMonth = monthOf(anchor)

  /** Up to three kind-coloured dots per day cell. */
  const dotsFor = (date: ISODate) =>
    state.events
      .filter((e) => e.date === date)
      .slice(0, 3)
      .map((e) => hueOf(e.kind))

  const dayEvents = eventsOn(state, selected)

  return (
    <>
      <ScreenHead
        title={tr('calendar.title')}
        sub={view === 'month' ? fmtMonthYear(anchor) : tr('calendar.everythingAhead')}
        action={
          <Button variant="accent" onClick={() => setEditing('new')}>
            <IconPlus /> {tr('calendar.event')}
          </Button>
        }
      />

      <div className="tabs" role="tablist" aria-label={tr('calendar.title')}>
        {(['month', 'agenda'] as const).map((v) => (
          <button
            key={v}
            role="tab"
            className="tabs__tab"
            aria-selected={view === v}
            onClick={() => setView(v)}
          >
            {v === 'month' ? tr('calendar.month') : tr('calendar.agenda')}
          </button>
        ))}
      </div>

      {view === 'month' ? (
        <div className="cal__layout">
          <Card>
            <div className="row" style={{ marginBottom: 'var(--s-3)' }}>
              <Button
                icon
                variant="quiet"
                aria-label={tr('calendar.prevMonth')}
                onClick={() => setAnchor(shiftMonth(anchor, -1))}
              >
                <span className="icon-prev" style={{ display: 'grid' }}>
                  <IconChevron />
                </span>
              </Button>
              <div className="grow" style={{ textAlign: 'center', fontWeight: 600 }}>
                {fmtMonthYear(anchor)}
              </div>
              <Button
                icon
                variant="quiet"
                aria-label={tr('calendar.nextMonth')}
                onClick={() => setAnchor(shiftMonth(anchor, 1))}
              >
                <span className="icon-next" style={{ display: 'grid' }}>
                  <IconChevron />
                </span>
              </Button>
            </div>

            <div className="cal__grid" role="grid">
              {dow.map((d, i) => (
                <div key={i} className="cal__dow" role="columnheader">
                  {d}
                </div>
              ))}
              {grid.map((date) => {
                const dots = dotsFor(date)
                const cls = [
                  'cal__day',
                  monthOf(date) !== anchorMonth && 'cal__day--muted',
                  date === t && 'cal__day--today',
                  date === selected && 'cal__day--sel',
                ]
                  .filter(Boolean)
                  .join(' ')
                return (
                  <button
                    key={date}
                    className={cls}
                    onClick={() => setSelected(date)}
                    aria-label={tr('calendar.dayLabel', { date: fmtDate(date), n: dots.length })}
                    aria-pressed={date === selected}
                  >
                    <span>{num(Number(date.slice(8)))}</span>
                    <span className="cal__dots">
                      {dots.map((hue, i) => (
                        <span
                          key={i}
                          className="cal__dot"
                          style={{ background: `var(--h-${hue})` }}
                        />
                      ))}
                    </span>
                  </button>
                )
              })}
            </div>
          </Card>

          <div className="stack stack--sm">
            <div className="row">
              <Meta className="grow">
                {fmtDate(selected)} · {relDay(selected)}
              </Meta>
            </div>
            {dayEvents.length === 0 ? (
              <Empty title={tr('calendar.nothingOn')} body={tr('calendar.nothingOnBody')} />
            ) : (
              dayEvents.map((e) => (
                <EventRow
                  key={e.id}
                  event={e}
                  onOpen={() => setEditing(e)}
                  onToggle={() => updateEvent(e.id, { done: !e.done })}
                />
              ))
            )}
          </div>
        </div>
      ) : (
        <AgendaView />
      )}

      {editing && (
        <EventSheet
          initial={editing === 'new' ? { date: selected } : editing}
          onClose={() => setEditing(null)}
          onSave={(data) => {
            if (editing === 'new') addEvent(data)
            else updateEvent(editing.id, data)
            setEditing(null)
          }}
          onDelete={
            editing === 'new'
              ? undefined
              : () => {
                  removeEvent(editing.id)
                  setEditing(null)
                }
          }
        />
      )}
    </>
  )

  function AgendaView() {
    const list = upcomingEvents(state, t, 60)
    if (!list.length)
      return (
        <Empty title={tr('calendar.clearHorizon')} body={tr('calendar.clearHorizonBody')} />
      )

    // Group consecutive events by date so each day gets one heading.
    const groups: { date: ISODate; events: CalEvent[] }[] = []
    for (const e of list) {
      const last = groups[groups.length - 1]
      if (last && last.date === e.date) last.events.push(e)
      else groups.push({ date: e.date, events: [e] })
    }

    return (
      <div className="stack">
        {groups.map((g) => (
          <div key={g.date} className="stack stack--sm">
            <Meta>
              {fmtDate(g.date)} · {relDay(g.date)}
            </Meta>
            {g.events.map((e) => (
              <EventRow
                key={e.id}
                event={e}
                onOpen={() => {
                  setSelected(e.date)
                  setEditing(e)
                }}
                onToggle={() => updateEvent(e.id, { done: !e.done })}
              />
            ))}
          </div>
        ))}
      </div>
    )
  }
}

/* --------------------------------------------------------------- event row */

function EventRow({
  event,
  onOpen,
  onToggle,
}: {
  event: CalEvent
  onOpen: () => void
  onToggle: () => void
}) {
  const tr = useT()
  const hue = hueOf(event.kind)
  const kind = EVENT_KINDS.find((k) => k.id === event.kind)
  return (
    <div className={`event ${event.done ? 'event--done' : ''}`} style={hueVars(hue)}>
      <span className="tint__rule" />
      <button className="grow" style={{ textAlign: 'left' }} onClick={onOpen}>
        <div className="event__title">{event.title}</div>
        <div className="event__sub">
          {event.start ? `${event.start}${event.end ? `–${event.end}` : ''} · ` : ''}
          {kind ? tr(kind.labelKey as TKey) : ''}
          {event.notes ? ` · ${event.notes}` : ''}
        </div>
      </button>
      <button
        className="btn btn--quiet btn--icon"
        onClick={onToggle}
        aria-label={tr(event.done ? 'calendar.markNotDone' : 'calendar.markDone')}
        aria-pressed={!!event.done}
        style={{ alignSelf: 'center' }}
      >
        {event.done ? '↺' : '✓'}
      </button>
    </div>
  )
}

/* ------------------------------------------------------------- event sheet */

function EventSheet({
  initial,
  onClose,
  onSave,
  onDelete,
}: {
  initial: Partial<CalEvent>
  onClose: () => void
  onSave: (e: Omit<CalEvent, 'id'>) => void
  onDelete?: () => void
}) {
  const [title, setTitle] = useState(initial.title ?? '')
  const [date, setDate] = useState(initial.date ?? today())
  const [start, setStart] = useState(initial.start ?? '')
  const [end, setEnd] = useState(initial.end ?? '')
  const [kind, setKind] = useState<EventKind>(initial.kind ?? 'personal')
  const [notes, setNotes] = useState(initial.notes ?? '')
  const tr = useT()

  const submit = () => {
    if (!title.trim()) return
    onSave({
      title: title.trim(),
      date,
      start: start || undefined,
      end: end || undefined,
      kind,
      notes: notes.trim() || undefined,
      done: initial.done,
      applicationId: initial.applicationId,
    })
  }

  return (
    <Sheet
      title={tr(initial.id ? 'calendar.editEvent' : 'calendar.newEvent')}
      onClose={onClose}
      footer={
        <>
          <Button variant="accent" block onClick={submit} disabled={!title.trim()}>
            {tr('common.save')}
          </Button>
          {onDelete && (
            <Button variant="danger" icon onClick={onDelete} aria-label={tr('calendar.deleteEvent')}>
              <IconTrash />
            </Button>
          )}
        </>
      }
    >
      <Field label={tr('calendar.fieldTitle')}>
        <input
          className="input"
          dir="auto"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={tr('calendar.titlePlaceholder')}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
      </Field>

      <Field label={tr('calendar.fieldDate')}>
        <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>

      <div className="row">
        <Field label={tr('calendar.fieldStart')}>
          <input className="input" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
        </Field>
        <Field label={tr('calendar.fieldEnd')}>
          <input className="input" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
        </Field>
      </div>

      <div className="field">
        <span>{tr('calendar.fieldKind')}</span>
        <div className="row row--wrap" style={{ gap: 'var(--s-2)' }}>
          {EVENT_KINDS.map((k) => (
            <button
              key={k.id}
              className="chip"
              aria-pressed={kind === k.id}
              onClick={() => setKind(k.id)}
            >
              <span className="chip__dot" style={{ background: `var(--h-${k.hue})` }} />
              {tr(k.labelKey as TKey)}
            </button>
          ))}
        </div>
      </div>

      <Field label={tr('calendar.fieldNotes')}>
        <textarea
          className="textarea"
          dir="auto"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={tr('calendar.notesPlaceholder')}
        />
      </Field>
    </Sheet>
  )
}
